import { ProductList } from "@packages/product-list/ProductList";
import { PLPFeatureFlags, StandardProps } from "@/types/types";
import { transformProduct } from "@/utils/transform";
import { useExperienceString, useFeatureFlags } from "@/flags/flags";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { LoadingSpinner } from "@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import styles from "./ProductGrid.module.scss";
import { useRouteParams } from "@/hooks/useRouteParams";
import { useHasComeIntoView } from "@/hooks/useHasComeIntoView";
import { ParsedUrlQuery } from "querystring";
import { useChunkLoader } from "@/contexts/ProductListingDataContext/ProductListingDataContext";
import Head from "next/head";
import { generateSrcSet } from "@packages/product-list/generateSrcSet";
import { ProductListConfigName } from "@~/configs/productListConfigMap.ts";
import { getProductListFlags } from "./utils/getProductListFlags";

function getProductListConfig(
  flags: PLPFeatureFlags,
  query: ParsedUrlQuery,
): ProductListConfigName {
  const browsableWebsiteOnly = flags.isBrowsableWebsiteOnlyEnabled?.value;

  if (!browsableWebsiteOnly) {
    const horizontalFilters = flags.isHorizontalFiltersFlagOn?.value;
    if (horizontalFilters) {
      return query.cardSize === "small"
        ? "plp-fullWidth-small"
        : "plp-fullWidth-large";
    }

    const curatedCategory = flags.curatedCategoryCard?.value;
    if (curatedCategory) {
      return "plp-curated-categories";
    }
  }

  const listViewWithImageCarousel =
    flags.isListViewOn?.value &&
    flags.isListViewWithImageCarouselFlagOn?.value &&
    (!query.plpView || query.plpView === "list");
  if (listViewWithImageCarousel) {
    return browsableWebsiteOnly
      ? "non-interactive-list-view"
      : "plp-list-view-with-image-carousel";
  }

  const listView =
    flags.isListViewOn?.value && (!query.plpView || query.plpView === "list");
  if (listView) {
    return browsableWebsiteOnly ? "non-interactive-list-view" : "plp-list-view";
  }

  const largeCardsWithReviews =
    flags.isLargeCardsWithReviewsFlagOn?.value &&
    flags.isLargeCardSizeFlagOn?.value &&
    (!query.cardSize || query.cardSize === "large");
  if (largeCardsWithReviews) {
    return browsableWebsiteOnly
      ? "non-interactive-large-cards"
      : "plp-large-cards-with-reviews";
  }

  const largeCardSize =
    flags.isLargeCardSizeFlagOn?.value &&
    (!query.cardSize || query.cardSize === "large");
  if (largeCardSize) {
    return browsableWebsiteOnly
      ? "non-interactive-large-cards"
      : "plp-large-cards";
  }

  return browsableWebsiteOnly ? "non-interactive" : "plp";
}

export function ProductGrid({
  productListingData,
}: Pick<StandardProps, "productListingData">) {
  const flags = useFeatureFlags();
  const {
    ref: chunkLoadingSentinelRef,
    hasComeIntoView: chunkLoadingSentinelhasComeIntoView,
  } = useHasComeIntoView({ rootMargin: "1800px" });
  const route = useRouteParams();
  const router = useRouter();
  const experiencesRawString = useExperienceString();
  const { loadNextChunk, isLoadingChunk, isChunkLoadError } = useChunkLoader();
  const numericPageSize = Number(flags.pageSize?.value);
  const numericCurrentPage = Number(route.query.page || 1);

  const products = productListingData.products.map((product) =>
    transformProduct(product),
  );

  useEffect(() => {
    if (chunkLoadingSentinelhasComeIntoView && !isChunkLoadError) {
      loadNextChunk();
    }
  }, [chunkLoadingSentinelhasComeIntoView, loadNextChunk, isChunkLoadError]);

  const { src, srcSet } = generateSrcSet(
    products[0]?.variants[0]?.images.primary || "",
  );

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          fetchPriority="high"
          href={src}
          imageSrcSet={srcSet}
        />
      </Head>
      <div
        id="main-content"
        data-testid="component-grid-container"
        aria-label="Product listing page results"
        data-list-config={getProductListConfig(flags, router.query)}
      >
        <meta
          name="total-number-of-items"
          content={products.length.toString()}
        />
        <ProductList
          products={products}
          resultsInfo={{
            start: (numericCurrentPage - 1) * numericPageSize,
            end: numericCurrentPage * numericPageSize,
            pageSize: numericPageSize,
            total: productListingData.results,
            chunkSize: Number(flags.chunkSize?.value),
          }}
          flags={getProductListFlags(experiencesRawString)}
          configName={getProductListConfig(flags, router.query)}
          experiencesRawString={experiencesRawString}
        />
        {isChunkLoadError && (
          <Message
            type="error"
            title="Error loading the next set of products, tap 'Load more products' to try again."
            buttonText="Load more products"
            buttonProps={{
              onClick: loadNextChunk,
            }}
            className={styles.chunkLoadErrorMessage}
          />
        )}
        <div data-testid="chunkLoadingSentinel" ref={chunkLoadingSentinelRef}>
          {isLoadingChunk && (
            <div className={styles.chunkLoading}>
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
